import { FC, useContext, useEffect, useState, ReactNode } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import {
    useAuctionPricing,
    useAuctionState,
    useAuctionSecondChanceInfo,
} from 'dibs-auctions/exports/hooks';
import { useAuctionBidModalState } from '../hooks/useAuctionBidModalState';
import { trackAuctionBidClick } from 'dibs-auctions-bidding-modal/exports/bidModalTrackingHelpers';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { useSharedAuctionContext } from '../PdpSharedAuctionContext/PdpSharedAuctionContext';

// components
import { PdpSharedAuctionClosed } from '../PdpSharedAuctionClosed/PdpSharedAuctionClosed';
import { PdpSharedAuctionPreview } from '../PdpSharedAuctionPreview/PdpSharedAuctionPreview';
import { PdpSharedAuctionActionBid } from '../PdpSharedAuctionActions/PdpSharedAuctionActionBid';
import { PdpSharedAuctionUnavailable } from '../PdpSharedAuctionUnavailable/PdpSharedAuctionUnavailable';
import { PdpSharedAuctionSubscription } from '../PdpSharedAuctionSubscription/PdpSharedAuctionSubscription';
import { PdpSharedAuctionActionBinAndBid } from '../PdpSharedAuctionActions/PdpSharedAuctionActionBinAndBid';
import { PdpSharedAuctionActionCompletePurchase } from '../PdpSharedAuctionActions/PdpSharedAuctionActionCompletePurchase';
import { PdpSharedAuctionSecondChanceOffer } from '../PdpSharedAuctionActions/PdpSharedAuctionSecondChanceOffer';
import AuctionBiddingModal from 'dibs-auctions-bidding-modal/exports/AuctionBiddingModal';
import { BID_RESPONSE_ERRORS } from 'dibs-auctions-bidding-modal/exports/constants';

// types
import { PdpSharedAuctionInfo_item$key as Item } from './__generated__/PdpSharedAuctionInfo_item.graphql';
import { PdpSharedAuctionInfo_viewer$key as Viewer } from './__generated__/PdpSharedAuctionInfo_viewer.graphql';
import { OnBidPlaced } from 'dibs-auctions-bidding-modal/exports/types';

// styles
import styles from './PdpSharedAuctionInfo.scss';

type Props = {
    item: Item;
    viewer: Viewer;
    currency: string;
    promoCode: ReactNode;
};

type RelayVariables = {
    userId: string;
    isClient: boolean;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

export type OnAuctionBidClick = (hasBid?: boolean) => void;

const itemFragment = graphql`
    fragment PdpSharedAuctionInfo_item on Item {
        isSold
        serviceId
        isUnavailable
        relevantAuctionLot(page: pdp) {
            serviceId
            endDate
        }
        relevantAuctionSolution(page: pdp) {
            reserveMet
            highestBidderId
        }
        lastTransaction(buyerId: $userId) @include(if: $fetchUser) {
            isCanceled
            checkoutDate
            isWinningAuctionOrder
            isSecondChanceOfferActive
            mostRecentOffer {
                id
                serviceId
                isExpired
            }
        }
        ...useAuctionSecondChanceInfo_item @arguments(page: pdp)
        ...PdpSharedAuctionSubscription_item
        ...PriceDisplay_item
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...useAuctionState_item @arguments(page: pdp)
        ...useAuctionPricing_item @arguments(page: pdp)
        ...PdpSharedAuctionActionBinAndBid_item
        ...PdpSharedAuctionActionBid_item
        ...PdpSharedAuctionClosed_item
        ...PdpSharedAuctionActionCompletePurchase_item
        ...PdpSharedAuctionPreview_item
        ...PdpSharedAuctionSecondChanceOffer_item
        ...bidModalTrackingHelpers_item
    }
`;

const viewerFragment = graphql`
    fragment PdpSharedAuctionInfo_viewer on Viewer {
        ...PdpSharedAuctionPreview_viewer
        ...PdpSharedAuctionActionBid_viewer
        ...PdpSharedAuctionActionBinAndBid_viewer
    }
`;

export const PdpSharedAuctionInfo: FC<Props> = ({
    currency,
    item: itemRef,
    viewer: viewerRef,
    promoCode,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const {
        isPreviewAuction,
        isActiveAuction,
        isPausedAuction,
        isTransitionAuction,
        isUnavailableAuction,
        hasAuctionEndedWithBIN,
        hasAuctionEndedWithWinner,
        hasAuctionEndedWithNoWinner,
    } = useAuctionState({ item });
    const { isSecondChanceOfferSent } = useAuctionSecondChanceInfo({ item });
    const { isBuyItNowEligible } = useAuctionPricing({
        item,
        currency,
    });
    const { userId, isClient } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );
    const { isMobile } = useContext(ServerVarsContext);
    const { isSold, lastTransaction, relevantAuctionLot, relevantAuctionSolution, isUnavailable } =
        item || {};
    const {
        isBiddingDisabled,
        setIsBiddingDisabled,
        setShouldDisplayCountdown,
        dispatchUpdateAuctionItem,
    } = useSharedAuctionContext();

    const {
        mostRecentOffer,
        isWinningAuctionOrder,
        isSecondChanceOfferActive,
        checkoutDate: transactionCheckoutDate,
        isCanceled: isTransactionCanceled,
    } = lastTransaction || {};
    const { endDate, serviceId: auctionLotId } = relevantAuctionLot || {};
    const { reserveMet, highestBidderId } = relevantAuctionSolution || {};
    const itemIsUnavailable =
        isUnavailableAuction ||
        (hasAuctionEndedWithBIN && !isSold) || // item was sold with BIN and then canceled
        isUnavailable;
    const { isModalOpen, openModal, closeModal } = useAuctionBidModalState(
        !isBiddingDisabled && !!isActiveAuction
    );

    const onAuctionBidClick: OnAuctionBidClick = (hasBid = false) => {
        trackAuctionBidClick({ item, hasBid });
        openModal();
    };

    const [isCloseToReserve, setIsCloseToReserve] = useState(false);

    const onBidPlaced: OnBidPlaced = bidError => {
        if (bidError.errorType === BID_RESPONSE_ERRORS.CLOSE_TO_RESERVE) {
            setIsCloseToReserve(true);
        } else if (isCloseToReserve) {
            setIsCloseToReserve(false);
        }
    };

    useEffect(() => {
        if (!isTransitionAuction) {
            setIsBiddingDisabled(false);
        } else {
            setIsBiddingDisabled(true);
        }
    }, [endDate, isTransitionAuction, setIsBiddingDisabled]);

    //We alwasys want to display the countdown if this component mounts
    //We only hide it if the `content` variable is still null after all the checks
    useEffect(() => {
        setShouldDisplayCountdown(true);
    }, [setShouldDisplayCountdown]);

    let content = null;
    let hideBorder = false;

    if (isSold) {
        // Move sold to first check.  If item is sold always show sold experience
        content = (
            <PdpSharedAuctionClosed
                item={item}
                currency={currency}
                linkToBidHistory={!!reserveMet && !isSecondChanceOfferSent}
            />
        );
    } else if (itemIsUnavailable) {
        content = <PdpSharedAuctionUnavailable />;
    } else if (isSecondChanceOfferActive && !transactionCheckoutDate) {
        content = <PdpSharedAuctionSecondChanceOffer item={item} currency={currency} />;
    } else if (isPreviewAuction) {
        content = (
            <PdpSharedAuctionPreview
                item={item}
                currency={currency}
                promoCode={promoCode}
                viewer={viewer}
            />
        );
    } else if ((isActiveAuction || isTransitionAuction) && isBuyItNowEligible) {
        content = (
            <PdpSharedAuctionActionBinAndBid
                item={item}
                currency={currency}
                onAuctionBidClick={onAuctionBidClick}
                isBiddingDisabled={isBiddingDisabled}
                viewer={viewer}
            />
        );
    } else if (isActiveAuction || isTransitionAuction) {
        content = (
            <PdpSharedAuctionActionBid
                item={item}
                currency={currency}
                onAuctionBidClick={onAuctionBidClick}
                isBiddingDisabled={isBiddingDisabled}
                isCloseToReserve={isCloseToReserve}
                viewer={viewer}
            />
        );
    } else if (isPausedAuction || isSecondChanceOfferSent) {
        content = (
            <PdpSharedAuctionClosed
                item={item}
                currency={currency}
                linkToBidHistory={!!reserveMet && !isSecondChanceOfferSent}
            />
        );
        hideBorder = true;
    } else if (hasAuctionEndedWithWinner && isClient) {
        if (
            highestBidderId === userId &&
            (!lastTransaction ||
                (isWinningAuctionOrder &&
                    !transactionCheckoutDate &&
                    !mostRecentOffer?.isExpired &&
                    !isTransactionCanceled))
        ) {
            content = <PdpSharedAuctionActionCompletePurchase item={item} currency={currency} />;
        } else {
            content = <PdpSharedAuctionClosed item={item} currency={currency} linkToBidHistory />;
            hideBorder = true;
        }
    } else if (hasAuctionEndedWithNoWinner) {
        content = <PdpSharedAuctionClosed item={item} currency={currency} />;
        hideBorder = true;
    }

    //handles case when item gets relisted to marketplace and none of the above conditions are met
    if (!content) {
        setShouldDisplayCountdown(false);
    }

    return (
        <>
            {content && (
                <div
                    className={classnames(styles.container, {
                        [styles.divider]: !isSold && !itemIsUnavailable && !hideBorder,
                    })}
                >
                    {content}
                </div>
            )}
            {!isPreviewAuction && promoCode}
            <PdpSharedAuctionSubscription refetch={dispatchUpdateAuctionItem} item={item} />
            {isModalOpen && (
                <AuctionBiddingModal
                    currency={currency}
                    itemId={item?.serviceId || ''}
                    onClose={closeModal}
                    isMobile={!!isMobile}
                    isBiddingDisabled={isBiddingDisabled}
                    onBidPlaced={onBidPlaced}
                    auctionLotId={auctionLotId || ''}
                />
            )}
        </>
    );
};
